import { Placement, Floating } from '../UNSAFE_Floating';
import {
  useAnimationStatus,
  AnimationStatus,
  AnimationStatusProps,
  AnimationStates
} from '../hooks/PRIVATE_useAnimationStatus';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useEffect, useCallback, useState } from 'preact/hooks';
import { getTransformOrigin } from '../utils/PRIVATE_popupAnimationUtils';
import { ComponentProps } from 'preact';

/**
 * The placement and direction aware HTML element
 * The useAnimation hook do not allow to change dynamically the animation states,
 * but allows a function that returns animation states config. The function takes
 * the animated node of type <E extends HTMLElement> as an argument. This could be used
 * to extend HTMLElement with placement and direction.
 */
export type AnimationPopupElement = HTMLElement & {
  placement?: Placement;
  direction?: 'ltr' | 'rtl';
};

type PopupAnimationProps = Omit<AnimationStatusProps, 'animationStates'> & {
  /**
   * Specifies placement of the Popup relative to the anchor.
   */
  placement: Placement;

  /**
   * animationStates is the configuration of the animations, how the element should be animated
   */
  animationStates?: AnimationStates;

  /**
   * Triggered when placement or coordinates are changed after collision is detected
   */
  onPosition?: ComponentProps<typeof Floating>['onPosition'];
};

const animationStates = {
  opening: (node: AnimationPopupElement) => {
    return {
      //zoomIn
      from: {
        scaleX: 0,
        scaleY: 0,
        opacity: 0,
        transformOrigin: getTransformOrigin(node.placement, node.direction)
      },
      to: {
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        transformOrigin: getTransformOrigin(node.placement, node.direction)
      },
      options: {
        duration: 200
      }
    };
  },
  closing: (node: AnimationPopupElement) => {
    return {
      //zoomOut
      from: {
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        transformOrigin: getTransformOrigin(node.placement, node.direction)
      },
      to: {
        scaleX: 0,
        scaleY: 0,
        opacity: 0,
        transformOrigin: getTransformOrigin(node.placement, node.direction)
      },
      options: {
        duration: 200
      }
    };
  }
};

export function usePopupAnimation(props: PopupAnimationProps): {
  status: AnimationStatus;
  setAnimationElementRef: (node: HTMLElement | null) => void;
  onPosition: ComponentProps<typeof Floating>['onPosition'];
} {
  const {
    isOpen,
    isAnimatedOnMount,
    onTransitionEnd,
    placement,
    animationStates: propsAnimationStates,
    onPosition
  } = props;

  //The animationPlacement is used for the case of flipping.
  const [animationPlacement, setAnimationPlacement] = useState<Placement>(placement);
  const { direction } = useUser();
  const { setAnimationElementRef, animationElementRef, status } =
    useAnimationStatus<AnimationPopupElement>({
      isOpen: isOpen,
      isAnimatedOnMount: isAnimatedOnMount,
      animationStates: propsAnimationStates ?? animationStates,
      onTransitionEnd
    });
  const popupElementRef = animationElementRef;

  const onPositionAnimated = useCallback<Required<ComponentProps<typeof Floating>>['onPosition']>(
    (data) => {
      if (popupElementRef.current !== null) {
        // if flips happens we need to change the placement in the animations.
        // There are cases where animation is done before even running the useEffect below. Due to that we update current placement of the node here
        popupElementRef.current.placement = data.placement;
      }
      if (animationPlacement !== data.placement) {
        setAnimationPlacement(data.placement);
      }
      onPosition?.(data);
    },
    [onPosition, popupElementRef, animationPlacement]
  );

  //This is needed for the case if popup flips and changes the placement.
  useEffect(() => {
    if (popupElementRef != null && popupElementRef.current != null && status != 'unmounted') {
      if (popupElementRef.current.placement !== animationPlacement) {
        popupElementRef.current.placement = animationPlacement;
      }
      popupElementRef.current.direction = direction;
    }
  }, [popupElementRef, animationPlacement, direction, status]);
  return { setAnimationElementRef, status, onPosition: onPositionAnimated };
}
